import { Injectable, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Params, Router } from '@angular/router';
import { filter, startWith, map, distinctUntilChanged } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from '@env/environment';
import { AppBootstrapService } from './app-bootstrap.service';

type ModuleSlug = 'orders' | 'services' | 'messaging' | 'invoices' | 'unknown';

interface UrlContext {
  tenantId: string | null;
  searchValue: string | null;
  dateRange: string | null;
  currentPage: ModuleSlug;
}

@Injectable({ providedIn: 'root' })
export class UrlBootstrapListenerService {
  private router = inject(Router);
  private ar = inject(ActivatedRoute);
  private bootstrap = inject(AppBootstrapService);
  private destroyRef = inject(DestroyRef);

  constructor() {
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      startWith(null), // correr también al cargar
      map(() => {
        const { params, query } = collectAll(this.ar.snapshot.root);

        const currentPage = getCurrentPage(this.router, this.ar.snapshot.root);
        const ctx: UrlContext = {
          tenantId:    (params['tenantId'] ?? query['tenant']) ?? null,
          searchValue: query[environment.nameSearchValueParamUrl] ?? null,
          dateRange:   query[environment.nameDateRangeParamUrl] ?? null,
          currentPage
        };
        return ctx;
      }),
      distinctUntilChanged(shallowCtxEqual),
      takeUntilDestroyed(this.destroyRef)
    )
    .subscribe(ctx => this.bootstrap.bootstrapFromUrl(ctx));
  }
}

/** Reúne TODOS los params y queryParams del árbol (rutas anidadas/auxiliares). */
function collectAll(node: ActivatedRouteSnapshot): { params: Params; query: Params } {
  let params: Params = { ...node.params };
  let query:  Params = { ...node.queryParams };
  for (const child of node.children ?? []) {
    const c = collectAll(child);
    params = { ...params, ...c.params };
    query  = { ...query,  ...c.query  };
  }
  return { params, query };
}

/** Obtiene el módulo actual desde route data o, si no existe, del primer segmento primario. */
function getCurrentPage(router: Router, root: ActivatedRouteSnapshot): ModuleSlug {
  // 1) Prioriza data.module en la ruta más profunda (si lo definiste en tus routes)
  const byData = getModuleFromData(root);
  if (byData) return byData;

  // 2) Fallback: primer segmento del outlet primario
  const tree = router.parseUrl(router.url);
  const seg0 = tree.root.children['primary']?.segments[0]?.path?.toLowerCase() ?? '';
  if (seg0 === 'orders' || seg0 === 'services' || seg0 === 'messaging' || seg0 === 'invoices') {
    return seg0;
  }
  return 'unknown';
}

function getModuleFromData(node: ActivatedRouteSnapshot): ModuleSlug | null {
  // recorre al hijo más profundo y busca data.module
  let deepest: ActivatedRouteSnapshot = node;
  while (deepest.firstChild) deepest = deepest.firstChild;
  const mod = (deepest.data?.['module'] as string | undefined)?.toLowerCase();
  if (!mod) return null;
  return (['orders','services','messaging','invoices'].includes(mod) ? (mod as ModuleSlug) : 'unknown');
}

/** Comparador liviano para UrlContext */
function shallowCtxEqual(a: UrlContext, b: UrlContext): boolean {
  return a.tenantId    === b.tenantId &&
         a.searchValue === b.searchValue &&
         a.dateRange   === b.dateRange &&
         a.currentPage === b.currentPage;
}
